import React, { useState, useEffect } from "react";
import { X, Calendar, Clock, AlertCircle } from "lucide-react";
import { AVAILABLE_DAYS, TIME_SLOTS } from "../../constants/ai-lab-constants";

const ScheduleModal = ({
  isOpen,
  onClose,
  course,
  currentSchedule,
  occupiedSlots = [], // Array of { day, timeSlot, courseLabel } from other courses of this child
  onSave,
  onRemove
}) => {
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");

  // Sync state when course or schedule changes
  useEffect(() => {
    if (isOpen && course) {
      setSelectedDay(currentSchedule?.day || "");
      setSelectedTimeSlot(currentSchedule?.timeSlot || "");
    }
  }, [isOpen, course, currentSchedule]);

  if (!isOpen || !course) return null;

  // Check if slot is occupied on selected day
  const getOccupiedLabel = (timeSlotLabel) => {
    if (!selectedDay) return null;
    const occupied = occupiedSlots.find(
      (slot) => slot.day === selectedDay && slot.timeSlot === timeSlotLabel
    );
    return occupied ? occupied.courseLabel : null;
  };

  const handleSave = () => {
    if (selectedDay && selectedTimeSlot) {
      onSave(selectedDay, selectedTimeSlot);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-lg overflow-hidden z-10 animate-in fade-in zoom-in-95 duration-200 text-left">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <span className="text-2xl p-2 bg-white rounded-2xl shadow-xs border border-gray-100">{course.icon}</span>
            <div>
              <h3 className="font-extrabold text-gray-900 text-base">{course.label}</h3>
              <p className="text-xs text-gray-500 font-semibold">Select day and time slot</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Day Selection */}
          <div className="space-y-2">
            <label className="flex items-center gap-1.5 text-xs font-black text-gray-400 uppercase tracking-wider">
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
              Select Day
            </label>
            <div className="flex flex-wrap gap-1.5">
              {AVAILABLE_DAYS.map((day) => {
                const isSelected = selectedDay === day;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      setSelectedDay(day);
                      // Clear timeslot if day changes to avoid collision or if it conflicts
                      setSelectedTimeSlot("");
                    }}
                    className={`px-3 py-2 rounded-xl text-xs font-bold border transition duration-150 ${isSelected
                      ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                      }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Slot Selection */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="flex items-center gap-1.5 text-xs font-black text-gray-400 uppercase tracking-wider">
                <Clock className="w-3.5 h-3.5 text-gray-400" />
                Select Time Slot
              </label>
              {!selectedDay && (
                <span className="text-[10px] text-amber-600 font-bold bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-md">
                  Select a day first
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TIME_SLOTS.map((slot) => {
                const isSelected = selectedTimeSlot === slot.label;
                const conflictCourse = getOccupiedLabel(slot.label);
                const isDisabled = !selectedDay || !!conflictCourse;

                return (
                  <button
                    key={slot.id}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => setSelectedTimeSlot(slot.label)}
                    className={`p-2 rounded-xl border text-[11px] font-bold text-center transition duration-150 flex flex-col justify-center items-center ${isSelected
                      ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                      : isDisabled
                        ? "bg-gray-50 text-gray-400 border-gray-150 cursor-not-allowed opacity-60"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                      }`}
                  >
                    <div className="font-bold">{slot.label}</div>
                    {conflictCourse && (
                      <span className="text-[8px] font-black text-red-500 uppercase tracking-wider mt-1 flex items-center gap-0.5">
                        <AlertCircle className="w-2 h-2" /> Booked ({conflictCourse})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <div>
            {onRemove && (
              <button
                type="button"
                onClick={() => {
                  onRemove();
                  onClose();
                }}
                className="text-xs font-black text-red-500 hover:text-red-700 hover:underline transition"
              >
                Remove Course
              </button>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-xl text-xs font-bold text-gray-500 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!selectedDay || !selectedTimeSlot}
              onClick={handleSave}
              className={`px-5 py-2 rounded-xl text-xs font-extrabold text-white shadow-sm transition ${selectedDay && selectedTimeSlot
                ? "bg-blue-600 hover:bg-blue-700 cursor-pointer"
                : "bg-gray-300 cursor-not-allowed"
                }`}
            >
              Save Schedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
